/**
 * Reservoir-level network-output views fronting `NetworkOutputs_Reservoir`.
 *
 * Every measure on the source — the scenario rate families and the
 * calculated-totals concern — is a per-row, run-indexed timeseries handle, so
 * each view is a timestamp-aligned, wide-typed reservoir-grain surface:
 * `createAlignedSeriesView` reads every handle, outer-joins them on the run
 * timestamp, and emits one row per `(reservoir × run timestamp)` with each
 * measure a typed scalar column carrying its unit, a `timestamp` column, and
 * identity + the reservoir direct-relation broadcast onto every row.
 *
 * The reservoir grain carries only the shared `SCENARIO_BASE` (phase rates + run
 * id) per scenario — no setpoints, system-opt extras or ad-hoc annotations
 * exist at this grain — so it maps the shared `SCENARIO_FAMILIES` registry
 * directly via `buildScenarioFamilyViews` (no `scenarioType` classification,
 * since there is no `comment` column to classify).
 *
 * The source names its reservoir grain ref `entityName`, so the keep renames it
 * to the canonical `reservoir` relation. Aligned views close over the
 * per-session CDF client, so the producer is a hook.
 */

import { createAlignedSeriesView } from '../createAlignedSeriesView.ts';
import type { VirtualView } from '../createReshapedView.ts';
import { CALC_TOTAL_RATES, col, IDENTITY, SCENARIO_BASE, type Shape } from '../shapes/index.ts';
import { buildScenarioFamilyViews, type ScenarioFamily, type ScenarioKind } from './optimizationScenarios.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import { NetworkOutputsReservoirView } from '#schema/generated/ipso/views.ts';

/**
 * Identity + the reservoir direct-relation kept on every NetworkOutputs_Reservoir
 * view. The source names the grain ref `entityName` (a Reservoir relation), so it
 * is renamed to the canonical `reservoir`.
 */
const RESERVOIR_KEEP: Shape = [
  ...IDENTITY,
  col('reservoir', 'unitless', 'Reference to the reservoir model entity.', { suffix: 'entityName' }),
];

/**
 * The reservoir grain's per-kind measure shape: every family carries only the
 * shared `SCENARIO_BASE` (phase rates + run id) — this grain has no setpoints,
 * system-opt extras or ad-hoc annotations.
 */
const RESERVOIR_SCENARIO_SHAPE_BY_KIND: Record<ScenarioKind, Shape> = {
  standard: SCENARIO_BASE,
  systemOpt: SCENARIO_BASE,
  adHocOpt: SCENARIO_BASE,
  adHocSim: SCENARIO_BASE,
};

/**
 * Build the nine per-scenario reservoir-grain aligned views from the shared
 * `SCENARIO_FAMILIES` registry (both system-optimized families included). Each
 * aligns its scenario-prefixed phase rates + run id into typed per-measure value
 * columns + a `timestamp` column over the RESERVOIR grain, broadcasting identity
 * + the `reservoir` relation onto every aligned row.
 */
const useReservoirScenarioRateViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return buildScenarioFamilyViews({
    grainStem: 'Reservoir',
    source: NetworkOutputsReservoirView,
    keep: RESERVOIR_KEEP,
    shapeByKind: RESERVOIR_SCENARIO_SHAPE_BY_KIND,
    describe: (family: ScenarioFamily) =>
      `Reservoir-grain ${family.label} oil/gas/water production rates aligned wide by run timestamp: one row per (reservoir × run timestamp), each rate a typed value column. Filter by reservoir (or join the reservoir entity) to scope to a reservoir.`,
    client,
    mintSeries,
  });
};

/** Build the single reservoir-grain calculated-totals concern view. */
const useReservoirConcernViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return [
    createAlignedSeriesView({
      name: 'ReservoirCalculatedTotals',
      description:
        'Network-solve calculated total production rates rolled up to the RESERVOIR grain, aligned wide by run timestamp: total oil/gas/water/liquid rates. Filter by reservoir (or join the reservoir entity) to scope to a reservoir.',
      source: NetworkOutputsReservoirView,
      keep: RESERVOIR_KEEP,
      groups: { shape: CALC_TOTAL_RATES },
      client,
      mintSeries,
    }),
  ];
};

/**
 * Build every RESERVOIR-grain network-output surface fronting
 * `NetworkOutputs_Reservoir`: the nine scenario rate views plus the
 * calculated-totals concern, all timestamp-aligned and wide-typed, closing over
 * the per-session CDF client.
 */
export const useNetworkOutputsReservoirViews = (): readonly VirtualView[] => [
  ...useReservoirScenarioRateViews(),
  ...useReservoirConcernViews(),
];
